import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonDropdown } from '@amzn/awsui-components-react';
import { GrowType, RetainReview, type AssetSummary, type StorylineSummary } from '../../../client/IvtHubClient';
import { buildStructuredTree, buildMenuTree, buildGrowTree, isNew } from '../utils/assetGrouping';
import { GROW_META, metricValue } from './AssetGrowCells';
import type { AssetsView } from './AssetsViewSwitch';
import { displayAreaName } from '../../SolutionMapPage/utils/spanCoverage';
import { useTreeUniverse } from '../hooks/useTreeUniverse';
import { useTreeMode } from './CollapsibleTreePanel';

/**
 * AssetNavTree — left nav tree of the Asset Detail page. Two projections of the
 * SAME asset set (asset-domain-v2-design.md §3.2):
 *   - Simplified: Industry → Asset  (+ Topic → Asset)  — flat, zero-derivation.
 *   - Structured: Industry → Sales Area → Use Case → Asset  (+ Topic → Sub-topic
 *     → Asset), from AssetSummary.solutionMapLinks / topicLinks. An asset with N
 *     UC links appears under EACH of its (industry, salesArea, useCase) leaves;
 *     assets with neither link fall under "未分类".
 *   - GROW (DAT-9159): Industry → GROW type → Asset, each asset prefixed with its
 *     metric and sorted by it; assets with no type fall under "未设置". No Topic
 *     section (a Topic-anchored asset has no industry).
 *
 * The selected asset's ancestor path auto-expands (every mode). Switching mode
 * or asset re-seeds the expanded set.
 */

interface Props {
  assets: AssetSummary[];
  selectedAssetId: string | null;
  /** When set, the "Industry" / "Topic" section labels become clickable and call
   *  this with the matching Overview view. Overview switches its live view; Detail
   *  persists + navigates to /ivt/assets. Omit → labels are plain text (未分类 is
   *  never clickable — the Overview has no such view). */
  onSectionClick?: (view: AssetsView) => void;
  /** When set, Structured-mode Use Case leaf LABELS become clickable and call this
   *  with the UC's solutionMapId (→ the UC detail page). A leaf without a
   *  solutionMapId stays non-clickable. Simplified mode has no UC level. */
  onUseCaseClick?: (solutionMapId: number) => void;
  /** Structured mode: the currently-open UC (UC detail page) — highlights its leaf
   *  row (light-blue bg), same treatment as the selected storyline. */
  selectedUseCaseId?: number | null;
  // ── Menu ("View by Storyline") mode props (storyline-design.md M2) ───────────
  /** Storylines skeleton (ListStorylines) — drives the Menu tree's Industry →
   *  Storyline nodes (incl. empty 0-asset storylines). Empty/omitted → Menu tree
   *  shows only Unassigned buckets. Ignored in the other modes. */
  storylines?: StorylineSummary[];
  /** Industry universe for the Menu tree (ListIndustries names) — seeds industries
   *  with no storyline yet. Falls back to useTreeUniverse().industries when omitted. */
  menuIndustries?: string[];
  /** Menu: storyline node header clicked (not an asset) → navigate to the storyline
   *  detail page. Omit → node label just toggles expand. */
  onStorylineClick?: (storylineId: string) => void;
  /** Menu: industry row-end `+` → open Create Storyline modal (industry prefilled). */
  onCreateStoryline?: (industry: string) => void;
  /** Menu: storyline node `⋮ → Edit` → open Edit Storyline modal. */
  onEditStoryline?: (storylineId: string) => void;
  /** Menu: storyline node `⋮ → Delete Storyline` → confirm + delete. */
  onDeleteStoryline?: (storylineId: string) => void;
  /** Menu: the currently-open storyline (detail page) — highlights its node. */
  selectedStorylineId?: string | null;
}

interface SimpleGroup {
  key: string; // industry name or topic name
  kind: 'industry' | 'topic';
  assets: AssetSummary[];
}

/** Simplified projection: industries (alphabetical) then topics. When `universe`
 *  is given, EVERY industry + topic name is seeded (empty ones show with a (0)). */
function buildSimplifiedGroups(
  assets: AssetSummary[],
  universe?: { industries?: string[]; topicNames?: string[] },
): SimpleGroup[] {
  const byIndustry = new Map<string, AssetSummary[]>();
  const byTopic = new Map<string, AssetSummary[]>();
  const push = (m: Map<string, AssetSummary[]>, key: string, a: AssetSummary) => {
    const list = m.get(key);
    if (list) list.push(a);
    else m.set(key, [a]);
  };
  // Seed the full universe first (empty groups), assets then fill them.
  for (const i of universe?.industries ?? []) if (i && !byIndustry.has(i)) byIndustry.set(i, []);
  for (const t of universe?.topicNames ?? []) if (t && !byTopic.has(t)) byTopic.set(t, []);
  for (const a of assets) {
    if (a.industry) push(byIndustry, a.industry, a);
    else if (a.topic) push(byTopic, a.topic, a);
  }
  const industryGroups: SimpleGroup[] = [...byIndustry.keys()]
    .sort((x, y) => x.localeCompare(y, 'en'))
    .map((key) => ({ key, kind: 'industry' as const, assets: byIndustry.get(key)! }));
  const topicGroups: SimpleGroup[] = [...byTopic.keys()]
    .sort((x, y) => x.localeCompare(y, 'en'))
    .map((key) => ({ key, kind: 'topic' as const, assets: byTopic.get(key)! }));
  return [...industryGroups, ...topicGroups];
}

// Expanded branch keys persist to sessionStorage so the tree KEEPS its open branches
// when you navigate between pages (Overview / Asset detail / UC detail each mount a
// fresh AssetNavTree — plain useState would reset to collapsed every time).
const EXPANDED_STORAGE_KEY = 'ivt.assetTree.expanded';

function loadExpanded(): Set<string> {
  try {
    const raw = sessionStorage.getItem(EXPANDED_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr.filter((k): k is string => typeof k === 'string'));
    }
  } catch {
    /* private mode / corrupt → empty */
  }
  return new Set();
}
function saveExpanded(keys: Set<string>) {
  try {
    sessionStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    /* ignore */
  }
}

export default function AssetNavTree({
  assets,
  selectedAssetId,
  onSectionClick,
  onUseCaseClick,
  selectedUseCaseId,
  storylines,
  menuIndustries,
  onStorylineClick,
  onCreateStoryline,
  onEditStoryline,
  onDeleteStoryline,
  selectedStorylineId,
}: Props) {
  const navigate = useNavigate();
  // Mode is a shared store (CollapsibleTreePanel) so the toggle can live on the
  // "Menu" header row while this tree body reads the same live value. localStorage
  // -backed; persists across sessions + navigation.
  const [mode] = useTreeMode();
  // Full classification universe (all UCs / topics, even empty ones). Fetched here
  // so all three pages that reuse the tree get "list everything" with no change.
  const universe = useTreeUniverse();
  const simpleGroups = useMemo(() => buildSimplifiedGroups(assets, universe), [assets, universe]);
  const structured = useMemo(() => buildStructuredTree(assets, universe), [assets, universe]);
  // Menu ("View by Storyline"): Industry → Storyline → Asset + per-industry
  // Unassigned. Industries seed = explicit menuIndustries prop or the universe.
  const menuBranches = useMemo(
    () => buildMenuTree(assets, storylines ?? [], menuIndustries ?? universe.industries),
    [assets, storylines, menuIndustries, universe.industries],
  );

  const growBranches = useMemo(() => buildGrowTree(assets, universe.industries), [assets, universe.industries]);

  // Ancestor keys of the selected asset, so its whole path auto-expands. Keys are
  // namespaced per level (e.g. "ind:FSI", "sa:FSI/Claims", "uc:FSI/Claims/…") so a
  // name shared across branches doesn't collide.
  const selectedPath = useMemo(() => {
    const keys = new Set<string>();
    // Menu mode: reveal the currently-open storyline (detail page) even with NO asset
    // selected — expand its industry branch + its own leaf. Without this, navigating to
    // a storyline (e.g. right after creating it) lands on a page whose tree hides the
    // storyline inside a collapsed industry. Keyed off the ListStorylines skeleton so
    // it works before any asset link resolves. Keys mirror the render rowKeys exactly.
    if (mode === 'storyline' && selectedStorylineId) {
      const s = (storylines ?? []).find((x) => x.id === selectedStorylineId);
      if (s?.industry) {
        keys.add(`mind:${s.industry}`);
        keys.add(`sl:${s.industry}/${selectedStorylineId}`);
      }
    }
    const sel = assets.find((a) => a.assetId === selectedAssetId);
    if (!sel) return keys;
    if (mode === 'simplified') {
      // Namespace by kind to match renderSimple's rowKey (industry: / topic:), so
      // auto-expand targets the right group when an industry + topic share a name.
      if (sel.industry) keys.add(`industry:${sel.industry}`);
      else if (sel.topic) keys.add(`topic:${sel.topic}`);
      return keys;
    }
    if (mode === 'grow') {
      // Its industry + every GROW group it sits in (or 未设置). Keys mirror the render.
      if (sel.industry) {
        keys.add(`gind:${sel.industry}`);
        const types = sel.growTypes ?? [];
        if (types.length === 0) keys.add(`gunset:${sel.industry}`);
        for (const t of types) keys.add(`grow:${sel.industry}/${t}`);
      }
      return keys;
    }
    if (mode === 'storyline') {
      // Menu ("Storyline Menu"): reveal the selected asset by expanding its industry
      // branch + each storyline leaf it's linked to (or the Unassigned bucket if it
      // has none). Keys must match the render rowKeys exactly (mind: / sl: /
      // unassigned:). A storyline link's industry keys the branch (storyline is
      // per-industry); an unlinked asset reveals under its own industry's Unassigned.
      const links = (sel.storylineLinks ?? []).filter((l) => l.storylineId && l.industry);
      if (links.length === 0) {
        if (sel.industry) {
          keys.add(`mind:${sel.industry}`);
          keys.add(`unassigned:${sel.industry}`);
        }
      } else {
        for (const l of links) {
          keys.add(`mind:${l.industry}`);
          keys.add(`sl:${l.industry}/${l.storylineId}`);
        }
      }
      return keys;
    }
    for (const l of sel.solutionMapLinks ?? []) {
      if (l.industry && l.solutionArea && l.useCase) {
        keys.add(`ind:${l.industry}`);
        keys.add(`sa:${l.industry}/${l.solutionArea}`);
        keys.add(`uc:${l.industry}/${l.solutionArea}/${l.useCase}`);
      }
    }
    for (const l of sel.topicLinks ?? []) {
      if (l.topicName && l.subTopicName) {
        keys.add(`top:${l.topicName}`);
        keys.add(`st:${l.topicName}/${l.subTopicName}`);
      }
    }
    return keys;
  }, [assets, selectedAssetId, mode, selectedStorylineId, storylines]);

  // Seed from the persisted set (survives cross-page navigation) UNION the selected
  // asset's ancestor path (so the selection is revealed on first mount).
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = loadExpanded();
    for (const k of selectedPath) s.add(k);
    return s;
  });
  // When the selected asset changes, MERGE its ancestor path into `expanded` so
  // the selection is revealed — WITHOUT collapsing branches the user opened
  // themselves (union, not replace). Switching mode uses namespaced keys, so a
  // stale key from the other mode is simply inert. (Deps stringify the path so the
  // effect only runs on a real path change, not every render.) Persist so the open
  // branches survive navigating to another page.
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const k of selectedPath) next.add(k);
      saveExpanded(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [[...selectedPath].join('|')]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveExpanded(next); // persist so open branches survive cross-page navigation
      return next;
    });

  /** `prefix`: GROW Menu only — the asset's metric in front of its name. */
  const assetButton = (a: AssetSummary, prefix?: React.ReactNode) => {
    const label = a.name ?? a.useCase ?? a.assetId ?? '—';
    const selected = a.assetId === selectedAssetId;
    // New Assets (origin === ivt_incubated) get a small "New" tag before the name —
    // same isNew() source as the Overview green bar / Detail [NEW] badge.
    const showNew = isNew(a);
    return (
      <button
        type="button"
        key={a.assetId ?? label}
        title={label} /* hover shows the full name (labels truncate with … in CSS) */
        className={`ivt-asset-tree__item${selected ? ' ivt-asset-tree__item--selected' : ''}`}
        onClick={() => a.assetId && navigate(`/ivt/assets/${a.assetId}`)}
      >
        {prefix}
        {showNew && <span className="ivt-asset-tree__new-tag">New</span>}
        {label}
      </button>
    );
  };

  /** A section label ("Industry (N)" / "Topic (N)"). If `view` is given AND the
   *  parent supplied onSectionClick, it renders as a clickable button that jumps to
   *  that Overview view; otherwise it's plain text (e.g. 未分类, or Detail with no
   *  handler). `first` drops the top divider (Industry is always first); mixed
   *  button/div tags make CSS :first-of-type unreliable, so the caller states it. */
  const sectionLabel = (text: string, first: boolean, view?: AssetsView) => {
    const cls = `ivt-asset-tree__section-label${first ? '' : ' ivt-asset-tree__section-label--divided'}`;
    if (view && onSectionClick) {
      return (
        <button
          type="button"
          className={`${cls} ivt-asset-tree__section-label--link`}
          onClick={() => onSectionClick(view)}
        >
          {text}
        </button>
      );
    }
    return <div className={cls}>{text}</div>;
  };

  /** A collapsible row (caret + label + count) used at every non-leaf level. When
   *  `onLabelClick` is given, the LABEL becomes its own clickable button (navigates)
   *  while the caret still just toggles — rendered as sibling buttons (not nested,
   *  which is invalid HTML). Used for clickable Use Case leaves. */
  const branchRow = (
    rowKey: string,
    label: string,
    count: number,
    depth: number,
    children: React.ReactNode,
    onLabelClick?: () => void,
    selected = false,
  ) => {
    const open = expanded.has(rowKey);
    return (
      <div className="ivt-asset-tree__group" key={rowKey}>
        <div
          className={`ivt-asset-tree__grouphead${selected ? ' ivt-asset-tree__grouphead--selected' : ''}`}
          style={{ paddingLeft: 4 + depth * 10 }}
        >
          <button
            type="button"
            className="ivt-asset-tree__caret-btn"
            aria-expanded={open}
            aria-label={open ? `收起 ${label}` : `展开 ${label}`}
            onClick={() => toggle(rowKey)}
          >
            <span className="ivt-asset-tree__caret">{open ? '▾' : '▸'}</span>
          </button>
          {onLabelClick ? (
            <button
              type="button"
              className="ivt-asset-tree__grouphead-label ivt-asset-tree__grouphead-label--link"
              title={label}
              onClick={onLabelClick}
            >
              {label}
            </button>
          ) : (
            <button
              type="button"
              className="ivt-asset-tree__grouphead-label"
              title={label}
              onClick={() => toggle(rowKey)}
            >
              {label}
            </button>
          )}
          <span className="ivt-asset-tree__count">({count})</span>
        </div>
        {open && children}
      </div>
    );
  };

  // Mode toggle (Figma): a list-view (≡) ICON button that opens a menu with
  // "Simplified Menu" / "Structured Menu"; the active mode is check-marked.
  // ── Simplified ─────────────────────────────────────────────────────────────
  if (mode === 'simplified') {
    const industryGroups = simpleGroups.filter((g) => g.kind === 'industry');
    const topicGroups = simpleGroups.filter((g) => g.kind === 'topic');
    const renderSimple = (g: SimpleGroup) =>
      branchRow(
        // Namespace the rowKey by kind so a same-named industry + topic don't share
        // one expand-state entry (structured mode already namespaces every level).
        `${g.kind}:${g.key}`,
        g.key,
        g.assets.length,
        0,
        <div className="ivt-asset-tree__items">
          {[...g.assets]
            .sort((a, b) => (a.name ?? a.useCase ?? '').localeCompare(b.name ?? b.useCase ?? '', 'en'))
            .map((a) => assetButton(a))}
        </div>,
      );
    // Section counts = distinct assets in that section (each asset lands in exactly
    // one Simplified group, so a plain sum is already distinct).
    const industryCount = industryGroups.reduce((n, g) => n + g.assets.length, 0);
    const topicCount = topicGroups.reduce((n, g) => n + g.assets.length, 0);
    return (
      <nav className="ivt-asset-tree" aria-label="Asset 导航">
        {sectionLabel(`Industry (${industryCount})`, true, 'industry')}
        {industryGroups.map(renderSimple)}
        {topicGroups.length > 0 && (
          <>
            {sectionLabel(`Topic (${topicCount})`, false, 'topic')}
            {topicGroups.map(renderSimple)}
          </>
        )}
      </nav>
    );
  }

  // ── GROW: Industry → GROW type → Asset (+ 未设置) ─────────────────────────────
  if (mode === 'grow') {
    // The metric in front of each asset: the computed figure, or for Retain the review
    // (the only coloured one — PM has not set good/bad thresholds for the others).
    const growPrefix = (type: GrowType, a: AssetSummary) => {
      let text: string;
      let tone = '';
      if (type === GrowType.RETAIN) {
        if (a.retainReview === RetainReview.PASSED) [text, tone] = ['Passed', ' is-passed'];
        else if (a.retainReview === RetainReview.FAILED) [text, tone] = ['Failed', ' is-failed'];
        else [text, tone] = ['未评审', ' is-muted'];
      } else {
        text = metricValue(type, a.growMetrics);
        if (text === '—') tone = ' is-muted';
      }
      return <span className={`ivt-asset-tree__grow-value ${GROW_META[type].className}${tone}`}>{text}</span>;
    };
    // An asset under two types shows twice but counts once. Each asset has one
    // industry, so the per-branch distinct counts add up to the section's.
    const branchCount = (b: (typeof growBranches)[number]) =>
      new Set([...b.groups.flatMap((g) => g.assets), ...b.unset]).size;
    const sectionCount = growBranches.reduce((n, b) => n + branchCount(b), 0);
    return (
      <nav className="ivt-asset-tree" aria-label="Asset 导航（按 GROW）">
        {sectionLabel(`Industry (${sectionCount})`, true, 'industry')}
        {growBranches.map((b) =>
          branchRow(
            `gind:${b.industry}`,
            b.industry,
            branchCount(b),
            0,
            <>
              {b.groups.map((g) =>
                branchRow(
                  `grow:${b.industry}/${g.type}`,
                  GROW_META[g.type].badge,
                  g.assets.length,
                  1,
                  <div className="ivt-asset-tree__items" style={{ paddingLeft: 2 * 10 }}>
                    {g.assets.map((a) => assetButton(a, growPrefix(g.type, a)))}
                  </div>,
                ),
              )}
              {b.unset.length > 0 &&
                branchRow(
                  `gunset:${b.industry}`,
                  '未设置',
                  b.unset.length,
                  1,
                  <div className="ivt-asset-tree__items" style={{ paddingLeft: 2 * 10 }}>
                    {b.unset.map((a) => assetButton(a))}
                  </div>,
                )}
            </>,
          ),
        )}
      </nav>
    );
  }

  // ── Menu ("View by Storyline"): Industry → Storyline → Asset + Unassigned ─────
  if (mode === 'storyline') {
    // A storyline leaf row: caret + clickable name (→ detail) + count + hover ⋮
    // (Edit / Delete). Built by hand (not branchRow) because it needs the extra ⋮
    // action menu; the caret+label+count layout mirrors branchRow for visual parity.
    const storylineRow = (
      industry: string,
      s: { storylineId: string; name: string; assets: AssetSummary[] },
    ) => {
      const rowKey = `sl:${industry}/${s.storylineId}`;
      const open = expanded.has(rowKey);
      const active = selectedStorylineId === s.storylineId;
      return (
        <div className="ivt-asset-tree__group" key={rowKey}>
          {/* Selected (open on the detail page) → whole-row light-blue bg. The label
              stays blue+bold (clickable); the row bg is what marks selection, so it
              doesn't collide with the hover underline. */}
          <div
            className={`ivt-asset-tree__grouphead${active ? ' ivt-asset-tree__grouphead--selected' : ''}`}
            style={{ paddingLeft: 4 + 1 * 10 }}
          >
            <button
              type="button"
              className="ivt-asset-tree__caret-btn"
              aria-expanded={open}
              aria-label={open ? `收起 ${s.name}` : `展开 ${s.name}`}
              onClick={() => toggle(rowKey)}
            >
              <span className="ivt-asset-tree__caret">{open ? '▾' : '▸'}</span>
            </button>
            <button
              type="button"
              className={`ivt-asset-tree__grouphead-label${
                onStorylineClick ? ' ivt-asset-tree__grouphead-label--link' : ''
              }`}
              title={s.name}
              onClick={() => (onStorylineClick ? onStorylineClick(s.storylineId) : toggle(rowKey))}
            >
              {s.name}
            </button>
            <span className="ivt-asset-tree__count">({s.assets.length})</span>
            {/* ⋮ Edit / Delete — only when handlers are wired (Overview). */}
            {(onEditStoryline || onDeleteStoryline) && (
              <ButtonDropdown
                variant="icon"
                ariaLabel={`${s.name} 操作`}
                items={[
                  { id: 'edit', text: 'Edit', disabled: !onEditStoryline },
                  { id: 'delete', text: 'Delete Storyline', disabled: !onDeleteStoryline },
                ]}
                onItemClick={({ detail }) => {
                  if (detail.id === 'edit') onEditStoryline?.(s.storylineId);
                  else if (detail.id === 'delete') onDeleteStoryline?.(s.storylineId);
                }}
              />
            )}
          </div>
          {open && (
            <div className="ivt-asset-tree__items" style={{ paddingLeft: 2 * 10 }}>
              {s.assets.map((a) => assetButton(a))}
            </div>
          )}
        </div>
      );
    };

    // Section count = distinct assets across this Menu tree (an asset under N
    // storylines counts once; Unassigned assets included).
    const menuDistinct = new Set<string>();
    for (const b of menuBranches) {
      for (const s of b.storylines) for (const a of s.assets) menuDistinct.add(a.assetId ?? '');
      for (const a of b.unassigned) menuDistinct.add(a.assetId ?? '');
    }

    return (
      <nav className="ivt-asset-tree" aria-label="Asset 导航（按 Storyline）">
        {sectionLabel(`Industry (${menuDistinct.size})`, true, 'industry')}
        {menuBranches.map((b) => {
          const rowKey = `mind:${b.industry}`;
          const open = expanded.has(rowKey);
          // DISTINCT assets in this industry branch — an asset linked to N storylines
          // shows as N rows when expanded, but the branch count (like the section
          // count above) tallies it ONCE. Storyline-multi-link is M:N, so summing
          // per-storyline lengths would over-count shared assets.
          const branchDistinct = new Set<string>();
          for (const s of b.storylines) for (const a of s.assets) branchDistinct.add(a.assetId ?? '');
          for (const a of b.unassigned) branchDistinct.add(a.assetId ?? '');
          const branchCount = branchDistinct.size;
          return (
            <div className="ivt-asset-tree__group" key={rowKey}>
              <div className="ivt-asset-tree__grouphead" style={{ paddingLeft: 4 }}>
                <button
                  type="button"
                  className="ivt-asset-tree__caret-btn"
                  aria-expanded={open}
                  aria-label={open ? `收起 ${b.industry}` : `展开 ${b.industry}`}
                  onClick={() => toggle(rowKey)}
                >
                  <span className="ivt-asset-tree__caret">{open ? '▾' : '▸'}</span>
                </button>
                <button
                  type="button"
                  className="ivt-asset-tree__grouphead-label"
                  title={b.industry}
                  onClick={() => toggle(rowKey)}
                >
                  {b.industry}
                </button>
                {/* Count hugs the industry name (like every other tree row). */}
                <span className="ivt-asset-tree__count">({branchCount})</span>
                {/* + = Create Storyline (industry prefilled). Cloudscape inline-icon
                    (add-plus) — same icon as the title-row "Add to Storyline" button.
                    margin-left:auto (see .ivt-asset-tree__add-btn) pins it to the far
                    right, so its x is fixed regardless of the count's digit width. */}
                {onCreateStoryline && (
                  <span className="ivt-asset-tree__add-btn">
                    <Button
                      variant="inline-icon"
                      iconName="add-plus"
                      ariaLabel={`在 ${b.industry} 下新建 Storyline`}
                      onClick={() => onCreateStoryline(b.industry)}
                    />
                  </span>
                )}
              </div>
              {open && (
                <>
                  {b.storylines.map((s) => storylineRow(b.industry, s))}
                  {b.unassigned.length > 0 &&
                    branchRow(
                      `unassigned:${b.industry}`,
                      'Unassigned',
                      b.unassigned.length,
                      1,
                      <div className="ivt-asset-tree__items" style={{ paddingLeft: 2 * 10 }}>
                        {b.unassigned.map((a) => assetButton(a))}
                      </div>,
                    )}
                </>
              )}
            </div>
          );
        })}
      </nav>
    );
  }

  // ── Structured: Industry → Sales Area → Use Case → Asset (+ Topic → Sub-topic) ─
  const branchAssetCount = (b: { nodes: { leaves: { assets: AssetSummary[] }[] }[] }) =>
    b.nodes.reduce((n, node) => n + node.leaves.reduce((m, lf) => m + lf.assets.length, 0), 0);

  // Section count = DISTINCT assets across all branches (an asset with N UC links
  // appears under N leaves, so a raw sum would double-count — dedupe by assetId).
  const distinctAssetCount = (branches: typeof structured.industries) => {
    const ids = new Set<string>();
    for (const b of branches)
      for (const node of b.nodes)
        for (const lf of node.leaves) for (const a of lf.assets) ids.add(a.assetId ?? '');
    return ids.size;
  };
  const industrySectionCount = distinctAssetCount(structured.industries);
  const topicSectionCount = distinctAssetCount(structured.topics);

  return (
    <nav className="ivt-asset-tree" aria-label="Asset 导航">
      {sectionLabel(`Industry (${industrySectionCount})`, true, 'industry')}
      {structured.industries.map((ind) =>
        branchRow(
          `ind:${ind.key}`,
          ind.key,
          branchAssetCount(ind),
          0,
          ind.nodes.map((sa) =>
            branchRow(
              `sa:${ind.key}/${sa.key}`,
              // Relabel the '_span_' sales-area sentinel to "Cross-Area" (shared
              // helper); rowKey stays sa.key so expand-state is unaffected.
              displayAreaName(sa.key),
              sa.leaves.reduce((m, lf) => m + lf.assets.length, 0),
              1,
              sa.leaves.map((uc) =>
                branchRow(
                  `uc:${ind.key}/${sa.key}/${uc.key}`,
                  uc.key,
                  uc.assets.length,
                  2,
                  <div className="ivt-asset-tree__items" style={{ paddingLeft: 3 * 10 }}>
                    {uc.assets.map((a) => assetButton(a))}
                  </div>,
                  // UC label → UC detail (only when a handler is wired AND the leaf
                  // carries a solutionMapId, the stable route key).
                  onUseCaseClick && uc.solutionMapId != null
                    ? () => onUseCaseClick(uc.solutionMapId as number)
                    : undefined,
                  // Highlight this UC leaf when it's the one open on the UC detail page.
                  uc.solutionMapId != null && uc.solutionMapId === selectedUseCaseId,
                ),
              ),
            ),
          ),
        ),
      )}

      {structured.topics.length > 0 && (
        <>
          {sectionLabel(`Topic (${topicSectionCount})`, false, 'topic')}
          {structured.topics.map((t) =>
            branchRow(
              `top:${t.key}`,
              t.key,
              branchAssetCount(t),
              0,
              t.nodes[0].leaves.map((st) =>
                branchRow(
                  `st:${t.key}/${st.key}`,
                  st.key,
                  st.assets.length,
                  1,
                  <div className="ivt-asset-tree__items" style={{ paddingLeft: 2 * 10 }}>
                    {st.assets.map((a) => assetButton(a))}
                  </div>,
                ),
              ),
            ),
          )}
        </>
      )}

      {structured.unclassified.length > 0 && (
        <>
          {/* 未分类 = assets with no UC link AND no topic link — a 3rd category
              PARALLEL to Industry / Topic (not nested under Topic). Flat list, so
              length is already the distinct count. NOT clickable — the Overview has
              no "未分类" view. */}
          {sectionLabel(`未分类 (${structured.unclassified.length})`, false)}
          <div className="ivt-asset-tree__items">{structured.unclassified.map((a) => assetButton(a))}</div>
        </>
      )}
    </nav>
  );
}
